import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import AdminNav from '../components/AdminNav';

export default function SurveyNew() {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [slug, setSlug] = useState('');
    const [questions, setQuestions] = useState([]);
    const [title_en, setTitleEn] = useState('');
    const [description_en, setDescriptionEn] = useState('');
    const [username, setUsername] = useState('');
    const [role, setRole] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            const { data } = await supabase.auth.getSession();
            if (!data.session) return;
            const { data: userData } = await supabase
                .from('users')
                .select('username, role')
                .eq('email', data.session.user.email)
                .single();
            if (userData) { setUsername(userData.username); setRole(userData.role); }
        };
        fetchUser();
    }, []);

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/login');
    };

    const generateSlug = (val) => {
        return val.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    };

    const handleTitleChange = (e) => {
        setTitle(e.target.value);
        setSlug(generateSlug(e.target.value));
    };

    const addQuestion = (type) => {
        setQuestions([...questions, {
            tempId: Date.now(),
            label: '',
            label_en: '',
            type,
            options: type === 'checkbox' || type === 'radio' || type === 'select' ? [''] : [],
            options_en: type === 'checkbox' || type === 'radio' || type === 'select' ? [''] : [],
            required: false,
            position: questions.length,
        }]);
    };

    const updateQuestion = (tempId, field, value) => {
        setQuestions(questions.map(q => q.tempId === tempId ? { ...q, [field]: value } : q));
    };

    const updateOption = (tempId, index, value) => {
        setQuestions(questions.map(q => {
            if (q.tempId !== tempId) return q;
            const newOptions = [...q.options];
            newOptions[index] = value;
            return { ...q, options: newOptions };
        }));
    };

    const addOption = (tempId) => {
        setQuestions(questions.map(q => q.tempId === tempId ? { ...q, options: [...q.options, ''] } : q));
    };

    const removeOption = (tempId, index) => {
        setQuestions(questions.map(q => {
            if (q.tempId !== tempId) return q;
            return { ...q, options: q.options.filter((_, i) => i !== index) };
        }));
    };

    const removeQuestion = (tempId) => {
        setQuestions(questions.filter(q => q.tempId !== tempId));
    };

    const moveQuestion = (tempId, dir) => {
        const idx = questions.findIndex(q => q.tempId === tempId);
        const newQ = [...questions];
        const swap = idx + dir;
        if (swap < 0 || swap >= newQ.length) return;
        [newQ[idx], newQ[swap]] = [newQ[swap], newQ[idx]];
        setQuestions(newQ);
    };

    const handleSave = async (publish = false) => {
        if (!title || !slug) return alert('Titre et slug requis.');
        setSaving(true);

        const { data: survey, error } = await supabase
            .from('surveys')
            .insert([{ title, title_en: title_en || null, description, description_en: description_en || null, slug, published: publish }])
            .select()
            .single();

        if (error) { alert('Erreur: ' + error.message); setSaving(false); return; }

        const questionsToInsert = questions.map((q, i) => ({
            survey_id: survey.id,
            label: q.label,
            label_en: q.label_en || null,
            type: q.type,
            options: q.options.filter(o => o.trim() !== ''),
            options_en: q.options_en?.filter(o => o.trim() !== '') || [],
            required: q.required,
            position: i,
        }));

        if (questionsToInsert.length > 0) {
            await supabase.from('survey_questions').insert(questionsToInsert);
        }

        setSaving(false);
        navigate(`/admin/surveys/${survey.id}`);
    };

    const updateOptionEn = (tempId, index, value) => {
        setQuestions(questions.map(q => {
            if (q.tempId !== tempId) return q;
            const newOptions = [...(q.options_en || [])];
            newOptions[index] = value;
            return { ...q, options_en: newOptions };
        }));
    };
    const inputStyle = { width: '100%', padding: '8px 10px', border: '0.5px solid #e0e0e0', borderRadius: '6px', fontSize: '13px', fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif', color: '#333', boxSizing: 'border-box', outline: 'none', background: 'white' };
    const labelStyle = { display: 'block', fontSize: '10px', fontWeight: '500', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#888', marginBottom: '6px' };

    const TYPE_LABELS = { text: 'Texte court', textarea: 'Texte long', radio: 'Choix unique', checkbox: 'Choix multiple', select: 'Menu déroulant' };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif' }}>

            {/* Topbar */}
            <AdminNav
                username={username}
                role={role}
                onLogout={handleLogout}
                activePath="/admin/surveys"
            />
            {/* Action buttons */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', borderBottom: '0.5px solid #e0e0e0' }}>
                <p style={{ fontSize: '11px', color: '#888', cursor: 'pointer', margin: 0 }} onClick={() => navigate('/admin/surveys')}>← Back</p>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => handleSave(false)} disabled={saving} style={{ padding: '6px 14px', background: 'none', border: '0.5px solid #e0e0e0', borderRadius: '6px', fontSize: '12px', color: '#888', cursor: 'pointer' }}>
                        {saving ? 'Saving...' : 'Save draft'}
                    </button>
                    <button onClick={() => handleSave(true)} disabled={saving} style={{ padding: '6px 14px', background: 'black', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                        Publish →
                    </button>
                </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', background: '#f7f7f7', padding: '32px 24px' }}>
                <div style={{ maxWidth: '680px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* Info du survey */}
                    <div style={{ background: 'white', border: '0.5px solid #e0e0e0', borderRadius: '10px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <div>
                            <label style={labelStyle}>Slug <span style={{ color: '#ccc', textTransform: 'none', letterSpacing: 0, fontWeight: '400' }}>(URL publique)</span></label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '12px', color: '#aaa' }}>/s/</span>
                                <input value={slug} onChange={e => setSlug(generateSlug(e.target.value))} placeholder="student-dashboard-2026" style={{ ...inputStyle, flex: 1 }} />
                            </div>
                        </div>
                        <div>
                            <label style={labelStyle}>Titre <span style={{ color: '#ccc', fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(FR)</span></label>
                            <input value={title} onChange={handleTitleChange} placeholder="ex: Student Dashboard 2026" style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Titre <span style={{ color: '#ccc', fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(EN — optionnel)</span></label>
                            <input value={title_en} onChange={e => setTitleEn(e.target.value)} placeholder="ex: Student Dashboard 2026" style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Description <span style={{ color: '#ccc', fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(FR — optionnel)</span></label>
                            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                        </div>
                        <div>
                            <label style={labelStyle}>Description <span style={{ color: '#ccc', fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(EN — optionnel)</span></label>
                            <textarea value={description_en} onChange={e => setDescriptionEn(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                        </div>
                    </div>

                    {/* Questions */}
                    {questions.map((q, idx) => (
                        <div key={q.tempId} style={{ background: 'white', border: '0.5px solid #e0e0e0', borderRadius: '10px', padding: '18px 20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <span style={{ fontSize: '10px', fontWeight: '500', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#aaa' }}>{TYPE_LABELS[q.type]}</span>
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <button onClick={() => moveQuestion(q.tempId, -1)} style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: '14px' }}>↑</button>
                                    <button onClick={() => moveQuestion(q.tempId, 1)} style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: '14px' }}>↓</button>
                                    <button onClick={() => removeQuestion(q.tempId)} style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: '14px' }}>🗑</button>
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <div>
                                    <label style={labelStyle}>Question</label>
                                    <input value={q.label} onChange={e => updateQuestion(q.tempId, 'label', e.target.value)} placeholder="ex: Quel est ton plus grand défi ?" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Question <span style={{ color: '#ccc', fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(EN — optionnel)</span></label>
                                    <input value={q.label_en || ''} onChange={e => updateQuestion(q.tempId, 'label_en', e.target.value)} placeholder="ex: What is your biggest challenge?" style={inputStyle} />
                                </div>

                                {/* Options pour radio/checkbox/select */}
                                {['radio', 'checkbox', 'select'].includes(q.type) && (
                                    <div>
                                        <label style={labelStyle}>Options <span style={{ color: '#ccc', fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(FR)</span></label>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            {q.options.map((opt, i) => (
                                                <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                    <input value={opt} onChange={e => updateOption(q.tempId, i, e.target.value)} placeholder={`Option ${i + 1}`} style={{ ...inputStyle, flex: 1 }} />
                                                    <button onClick={() => removeOption(q.tempId, i)} style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: '14px' }}>✕</button>
                                                </div>
                                            ))}
                                            <button onClick={() => addOption(q.tempId)} style={{ background: 'none', border: '0.5px dashed #e0e0e0', borderRadius: '6px', padding: '6px', fontSize: '11px', color: '#aaa', cursor: 'pointer' }}>+ Ajouter une option</button>
                                        </div>
                                    </div>
                                )}
                                {['radio', 'checkbox', 'select'].includes(q.type) && (
                                    <div>
                                        <label style={labelStyle}>Options <span style={{ color: '#ccc', fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(EN — optionnel)</span></label>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            {q.options_en.map((opt, i) => (
                                                <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                    <input value={opt} onChange={e => updateOptionEn(q.tempId, i, e.target.value)} placeholder={`Option ${i + 1}`} style={{ ...inputStyle, flex: 1 }} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Requis toggle */}
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#555', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={q.required} onChange={e => updateQuestion(q.tempId, 'required', e.target.checked)} />
                                    Requis
                                </label>
                            </div>
                        </div>
                    ))}

                    {/* Ajouter une question */}
                    <div style={{ background: 'white', border: '0.5px dashed #e0e0e0', borderRadius: '10px', padding: '16px 20px' }}>
                        <p style={{ fontSize: '10px', fontWeight: '500', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#aaa', marginBottom: '10px' }}>Ajouter une question</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {Object.entries(TYPE_LABELS).map(([type, label]) => (
                                <button key={type} onClick={() => addQuestion(type)} style={{ padding: '5px 12px', borderRadius: '20px', border: '0.5px solid #e0e0e0', background: 'white', fontSize: '12px', color: '#555', cursor: 'pointer' }}>
                                    + {label}
                                </button>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}